"use client";

import { useState } from "react";
import {
  adjustedCaloriesPerHour,
  formatTime,
  parseTime,
  pmCaloriesPerHour,
  splitFromWatts,
  timeForDistance,
  wattsFromSplit,
} from "@/lib/erg";

type Mode = "split" | "watts";

const DISTANCES = [500, 1000, 2000, 5000, 6000, 10000];

const inputClass =
  "mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-primary focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100";

export function PaceWattsCalculator() {
  const [mode, setMode] = useState<Mode>("split");
  const [splitInput, setSplitInput] = useState("2:00");
  const [wattsInput, setWattsInput] = useState("200");
  const [weightInput, setWeightInput] = useState("");

  let split = NaN;
  let watts = NaN;
  if (mode === "split") {
    split = parseTime(splitInput);
    if (split > 0) watts = wattsFromSplit(split);
  } else {
    watts = parseFloat(wattsInput);
    if (watts > 0) split = splitFromWatts(watts);
  }
  const valid = split > 0 && watts > 0 && Number.isFinite(split) && Number.isFinite(watts);
  const weight = parseFloat(weightInput);
  const hasWeight = weight > 0;

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-900">
      <div className="flex gap-2" role="tablist" aria-label="Calculate from">
        {(["split", "watts"] as const).map((m) => (
          <button
            key={m}
            role="tab"
            aria-selected={mode === m}
            onClick={() => setMode(m)}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              mode === m
                ? "bg-primary text-white"
                : "border border-gray-300 text-gray-700 hover:border-primary dark:border-gray-600 dark:text-gray-300"
            }`}
          >
            {m === "split" ? "I know my split" : "I know my watts"}
          </button>
        ))}
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {mode === "split" ? (
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Split (time per 500 m)
            <input
              className={inputClass}
              value={splitInput}
              onChange={(e) => setSplitInput(e.target.value)}
              placeholder="2:00"
              inputMode="decimal"
            />
          </label>
        ) : (
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Average watts
            <input
              className={inputClass}
              value={wattsInput}
              onChange={(e) => setWattsInput(e.target.value)}
              placeholder="200"
              inputMode="decimal"
            />
          </label>
        )}
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Body weight in lb (optional, for calories)
          <input
            className={inputClass}
            value={weightInput}
            onChange={(e) => setWeightInput(e.target.value)}
            placeholder="175"
            inputMode="decimal"
          />
        </label>
      </div>

      {valid ? (
        <div className="mt-6" aria-live="polite">
          <dl className="grid gap-3 sm:grid-cols-3">
            <Stat label="Split /500 m" value={formatTime(split)} />
            <Stat label="Watts" value={Math.round(watts).toString()} />
            <Stat
              label={hasWeight ? `Calories/hour at ${weight} lb` : "Calories/hour (PM display)"}
              value={Math.round(
                hasWeight ? adjustedCaloriesPerHour(watts, weight) : pmCaloriesPerHour(watts)
              ).toLocaleString()}
            />
          </dl>
          <h3 className="mt-6 text-sm font-semibold text-gray-900 dark:text-gray-100">
            Finish time at this pace
          </h3>
          <table className="mt-2 w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-400">
                <th className="py-1 font-medium">Distance</th>
                <th className="py-1 font-medium">Time</th>
              </tr>
            </thead>
            <tbody>
              {DISTANCES.map((d) => (
                <tr key={d} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-1 text-gray-700 dark:text-gray-300">{d.toLocaleString()} m</td>
                  <td className="py-1 font-mono text-gray-900 dark:text-gray-100">
                    {formatTime(timeForDistance(split, d))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-6 text-sm text-amber-700 dark:text-amber-400">
          Enter a split like 1:55 or 1:55.5, or a positive number of watts.
        </p>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-white p-3 dark:bg-gray-800">
      <dt className="text-xs text-gray-600 dark:text-gray-400">{label}</dt>
      <dd className="mt-1 font-mono text-2xl font-semibold text-gray-900 dark:text-gray-100">{value}</dd>
    </div>
  );
}
